import React from "react";
import { Link } from "react-router-dom";
import StatusBadge, { UrgencyBadge } from "./StatusBadge";
import { MapPin, Calendar, Route, Euro, Lock } from "lucide-react";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function RequestCard({ req, compact = false, score = null, matchType = null, currentProId = null }) {
  // Verrou de négociation actif pour CE pro
  const isLockedByMe = req.negotiating_pro_id && req.negotiating_pro_id === currentProId
    && req.negotiating_until && new Date(req.negotiating_until) > new Date();

  const lockMinutesLeft = isLockedByMe
    ? Math.ceil((new Date(req.negotiating_until) - new Date()) / 60000)
    : null;

  return (
    <Link
      to={`/requests/${req.request_id}`}
      data-testid={`request-card-${req.request_id}`}
      className={`group block bg-white rounded-md p-5 hover:-translate-y-0.5 hover:shadow-md transition-all ${
        isLockedByMe
          ? "border-2 border-blue-400 hover:border-blue-500"
          : "border border-slate-200 hover:border-orange-600"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <UrgencyBadge urgency={req.urgency} />
            {score !== null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-600 text-white text-[11px] font-bold px-2 py-0.5">
                {matchType === "reverse" ? "A/R" : "AXE"} · {Math.round(score)}
              </span>
            )}
            {isLockedByMe && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold px-2 py-0.5 border border-blue-200">
                <Lock className="w-2.5 h-2.5" /> Négociation · {lockMinutesLeft}min
              </span>
            )}
            {req.return_trip_match && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold px-2 py-0.5">
                ↩ Sur votre trajet retour
              </span>
            )}
          </div>
          <h3 className="font-display font-bold text-slate-900 text-base leading-tight truncate">{req.title}</h3>
        </div>
        <StatusBadge status={req.status} />
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-start gap-2 text-slate-700">
          <MapPin className="h-4 w-4 mt-0.5 text-orange-600 shrink-0" />
          <div className="min-w-0">
            <div className="font-medium truncate">{req.pickup.city}</div>
            <div className="text-xs text-slate-500">{req.pickup.country}</div>
          </div>
          <span className="text-slate-300">→</span>
          <div className="min-w-0">
            <div className="font-medium truncate">{req.dropoff.city}</div>
            <div className="text-xs text-slate-500">{req.dropoff.country}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 pt-1">
          <span className="inline-flex items-center gap-1">
            <Route className="h-3.5 w-3.5" /> {Math.round(req.distance_km || 0)} km
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" /> {formatDate(req.pickup_date)}
          </span>
          {req.budget_eur ? (
            <span className="inline-flex items-center gap-1 font-semibold text-slate-900">
              <Euro className="h-3.5 w-3.5" /> {req.budget_eur}
            </span>
          ) : null}
        </div>

        {!compact && req.description ? (
          <p className="text-xs text-slate-500 line-clamp-2 pt-1">{req.description}</p>
        ) : null}
      </div>
    </Link>
  );
}
